import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramMixedAirEnthalpy } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.airTempMixedAirEnthalpy.v1';

type Fields = {
  outsideAirEnthalpy: string;
  returnAirEnthalpy: string;
  outsideAirPercentage: string;
  mixedAirEnthalpy: string;
};
const EMPTY: Fields = {
  outsideAirEnthalpy: '',
  returnAirEnthalpy: '',
  outsideAirPercentage: '',
  mixedAirEnthalpy: '',
};

/**
 * Mixed Air Enthalpy — h_mixed = (%OA/100)×h_OA + (1-%OA/100)×h_RA
 */
const AirTemperatureMixedAirEnthalpyScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp } = useUnits();

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 3) {
      setError('Fill any 3 of the 4 fields to solve for the missing one.');
      return;
    }
    if (filled === 4) {
      setError('All 4 values are filled — clear one to solve for it.');
      return;
    }

    const oaEnthalpy = toImp(fields.outsideAirEnthalpy, 'enthalpy');
    const raEnthalpy = toImp(fields.returnAirEnthalpy, 'enthalpy');
    const oaPercent = parseFloat(fields.outsideAirPercentage);
    const mixedEnthalpy = toImp(fields.mixedAirEnthalpy, 'enthalpy');

    if (fields.mixedAirEnthalpy === '') {
      if (oaPercent < 0 || oaPercent > 100) {
        setError('Outside air percentage must be between 0 and 100%.');
        return;
      }
      setSolvedFor('mixedAirEnthalpy');
      set({ mixedAirEnthalpy: fromImp((oaPercent * oaEnthalpy + (100 - oaPercent) * raEnthalpy) / 100, 'enthalpy', 2) });
    } else if (fields.outsideAirEnthalpy === '') {
      if (oaPercent < 0 || oaPercent > 100) {
        setError('Outside air percentage must be between 0 and 100%.');
        return;
      }
      if (oaPercent === 0) {
        setError('Cannot calculate outside air enthalpy when OA% is 0%.');
        return;
      }
      setSolvedFor('outsideAirEnthalpy');
      set({ outsideAirEnthalpy: fromImp(((mixedEnthalpy * 100) - ((100 - oaPercent) * raEnthalpy)) / oaPercent, 'enthalpy', 2) });
    } else if (fields.returnAirEnthalpy === '') {
      if (oaPercent < 0 || oaPercent > 100) {
        setError('Outside air percentage must be between 0 and 100%.');
        return;
      }
      if (oaPercent === 100) {
        setError('Cannot calculate return air enthalpy when OA% is 100%.');
        return;
      }
      setSolvedFor('returnAirEnthalpy');
      set({ returnAirEnthalpy: fromImp(((mixedEnthalpy * 100) - (oaPercent * oaEnthalpy)) / (100 - oaPercent), 'enthalpy', 2) });
    } else if (fields.outsideAirPercentage === '') {
      if (oaEnthalpy === raEnthalpy) {
        setError('Outside air and return air enthalpy cannot be the same.');
        return;
      }
      const result = ((mixedEnthalpy - raEnthalpy) / (oaEnthalpy - raEnthalpy)) * 100;
      if (result < 0 || result > 100) {
        setError('Calculated outside air percentage is outside valid range (0–100%).');
        return;
      }
      setSolvedFor('outsideAirPercentage');
      set({ outsideAirPercentage: result.toFixed(1) });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.outsideAirEnthalpy && { label: 'Outside Air Enthalpy', value: fields.outsideAirEnthalpy, unit: unit('enthalpy'), emphasis: solvedFor === 'outsideAirEnthalpy' },
    fields.returnAirEnthalpy && { label: 'Return Air Enthalpy', value: fields.returnAirEnthalpy, unit: unit('enthalpy'), emphasis: solvedFor === 'returnAirEnthalpy' },
    fields.outsideAirPercentage && { label: 'OA Percentage', value: fields.outsideAirPercentage, unit: '%', emphasis: solvedFor === 'outsideAirPercentage' },
    fields.mixedAirEnthalpy && { label: 'Mixed Air Enthalpy', value: fields.mixedAirEnthalpy, unit: unit('enthalpy'), emphasis: solvedFor === 'mixedAirEnthalpy' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Mixed Air Enthalpy"
      subtitle="Calculate mixed air enthalpy based on outside air, return air, and mixing percentages"
      formula={
        <>
          <FormulaLine>h_m = (OA%/100)×h_OA + (1−OA%/100)×h_RA</FormulaLine>
          <FormulaNote>
            h_m = Mixed Air Enthalpy ({unit('enthalpy')}), OA% = Outside Air % (%), h_OA = OA Enthalpy ({unit('enthalpy')}), h_RA = RA Enthalpy ({unit('enthalpy')})
          </FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: h_m = (OA%/100)×h_OA + (1−OA%/100)×h_RA"
      diagram={<DiagramMixedAirEnthalpy />}
      outputSlots={{
        outside_air_pct: fields.outsideAirPercentage ? parseFloat(fields.outsideAirPercentage) : undefined,
        enthalpy_btu_lb: fields.mixedAirEnthalpy ? toImp(fields.mixedAirEnthalpy, 'enthalpy') : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.outside_air_pct != null && !fields.outsideAirPercentage) {
          patch.outsideAirPercentage = values.outside_air_pct.toFixed(1);
        }
        if (values.enthalpy_btu_lb != null && !fields.outsideAirEnthalpy) {
          patch.outsideAirEnthalpy = fromImp(values.enthalpy_btu_lb, 'enthalpy', 2);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Outside Air Enthalpy"
          value={fields.outsideAirEnthalpy}
          onChangeText={(v) => set({ outsideAirEnthalpy: v })}
          rightText={unit('enthalpy')}
          maxDecimals={2}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Return Air Enthalpy"
          value={fields.returnAirEnthalpy}
          onChangeText={(v) => set({ returnAirEnthalpy: v })}
          rightText={unit('enthalpy')}
          maxDecimals={2}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Outside Air Percentage"
          value={fields.outsideAirPercentage}
          onChangeText={(v) => set({ outsideAirPercentage: v })}
          rightText="%"
          maxDecimals={1}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Mixed Air Enthalpy"
          value={fields.mixedAirEnthalpy}
          onChangeText={(v) => set({ mixedAirEnthalpy: v })}
          rightText={unit('enthalpy')}
          maxDecimals={2}
          onSubmitEditing={calculate}
        />
      </View>
    </CalcShell>
  );
};

export default AirTemperatureMixedAirEnthalpyScreen;
